import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const LimitExhaustedBanner = ({ status, onUpgradeClick }) => {
    const { t, language } = useLanguage();
    const [expanded, setExpanded] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    
    if (!status || !status.quotas || dismissed) return null;
    
    const { quotas, tier, tierDescription } = status;
    
    const unlimitedTiers = ['enterprise', 'business'];
    if (unlimitedTiers.includes(tier)) return null;
    
    const isUnlimited = (limit) => limit === -1 || limit === null;
    
    const exhaustedLimits = [];
    
    if (!isUnlimited(quotas.crux?.limit) && quotas.crux?.percentage >= 100) {
        exhaustedLimits.push({
            type: 'crux',
            label: t('banners.limitExhausted.cruxAnalyses'),
            used: quotas.crux.used,
            limit: quotas.crux.limit,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            )
        });
    }
    
    if (!isUnlimited(quotas.pagespeed?.limit) && quotas.pagespeed?.percentage >= 100) {
        exhaustedLimits.push({
            type: 'pagespeed',
            label: t('banners.limitExhausted.pageSpeedAnalyses'),
            used: quotas.pagespeed.used,
            limit: quotas.pagespeed.limit,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            )
        });
    }
    
    if (!isUnlimited(quotas.domains?.limit) && quotas.domains?.percentage >= 100) {
        exhaustedLimits.push({
            type: 'domains',
            label: t('banners.limitExhausted.trackedDomains'),
            used: quotas.domains.used,
            limit: quotas.domains.limit,
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
            )
        });
    }
    
    if (exhaustedLimits.length === 0) return null;
    
    const getMidnightReset = () => {
        const now = new Date();
        const midnight = new Date(now);
        midnight.setDate(midnight.getDate() + 1);
        midnight.setHours(0, 0, 0, 0);
        const diffMs = midnight - now;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const locale = language === 'cs' ? 'cs-CZ' : 'en-US';
        return { hours, minutes, time: midnight.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) };
    };
    
    const resetInfo = getMidnightReset();
    
    const tierUpgrades = {
        guest: { 
            next: 'Free', 
            benefit: t('banners.limitExhausted.benefitGuest'),
            action: t('banners.limitExhausted.registerFree')
        },
        free: { 
            next: 'Pro', 
            benefit: t('banners.limitExhausted.benefitFree'),
            action: t('banners.limitExhausted.upgradeToTier').replace('{tier}', 'Pro')
        },
        pro: { 
            next: 'Premium', 
            benefit: t('banners.limitExhausted.benefitPro'),
            action: t('banners.limitExhausted.upgradeToTier').replace('{tier}', 'Premium')
        },
        premium: { 
            next: 'Enterprise', 
            benefit: t('banners.limitExhausted.benefitPremium'),
            action: t('banners.limitExhausted.contactUs')
        },
        enterprise: null,
        business: null
    };
    
    const upgrade = tierUpgrades[tier];
    
    const getLimitReasonText = (limitType) => {
        switch(limitType) {
            case 'crux':
                return t('banners.limitExhausted.cruxLimitReason');
            case 'pagespeed':
                return t('banners.limitExhausted.pageSpeedLimitReason');
            case 'domains':
                return t('banners.limitExhausted.domainsLimitReason');
            default:
                return t('banners.limitExhausted.defaultLimitReason');
        }
    };

    return (
        <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-red-500/10 border border-amber-500/30 rounded-xl overflow-hidden animate-fade-in">
            <div className="p-4">
                <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-amber-400 font-semibold">
                                {exhaustedLimits.length === 1 
                                    ? t('banners.limitExhausted.limitReached').replace('{label}', exhaustedLimits[0].label)
                                    : t('banners.limitExhausted.limitsReached').replace('{count}', exhaustedLimits.length)
                                }
                            </h3>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400">
                                {tierDescription}
                            </span>
                        </div>
                        
                        <p className="text-sm text-slate-400 mt-1">
                            {getLimitReasonText(exhaustedLimits[0].type)}
                        </p>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-3">
                            {exhaustedLimits.map(limit => (
                                <div key={limit.type} className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg">
                                    <span className="text-red-400">{limit.icon}</span>
                                    <span className="text-sm text-red-400 font-medium">
                                        {limit.used}/{limit.limit}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <button 
                        onClick={() => setDismissed(true)}
                        className="flex-shrink-0 text-slate-500 hover:text-slate-300 transition-colors p-1"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-700/50">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-slate-300">{t('banners.limitExhausted.waitForRenewal')}</div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                    {t('banners.limitExhausted.limitsRenewIn')} <span className="text-blue-400 font-medium">{resetInfo.hours}h {resetInfo.minutes}m</span>
                                </div>
                                <div className="text-xs text-slate-600 mt-0.5">
                                    {t('banners.limitExhausted.atMidnight').replace('{time}', resetInfo.time)}
                                </div>
                            </div>
                        </div>
                        
                        {upgrade && (
                            <div className="flex-1">
                                <button
                                    onClick={onUpgradeClick}
                                    className="w-full h-full min-h-[80px] p-4 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 rounded-lg transition-all group shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    <div className="flex items-center justify-center gap-3">
                                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                        </div>
                                        <div className="text-left">
                                            <div className="text-base font-bold text-white">
                                                {upgrade.action}
                                            </div>
                                            <div className="text-sm text-white/80 mt-0.5">
                                                {upgrade.benefit}
                                            </div>
                                        </div>
                                        <svg className="w-5 h-5 text-white/80 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="mt-3 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-400 transition-colors"
                >
                    <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    {expanded ? t('banners.limitExhausted.hideDetails') : t('banners.limitExhausted.showDetails')}
                </button>
                
                {expanded && (
                    <div className="mt-3 p-4 bg-slate-800/30 rounded-lg space-y-4 animate-fade-in">
                        <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2">{t('banners.limitExhausted.whyLimits')}</h4>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                {t('banners.limitExhausted.whyLimitsExplanation')}
                            </p>
                        </div>
                        
                        <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2">{t('banners.limitExhausted.tierComparison')}</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <div className={`p-2 rounded border ${tier === 'free' ? 'border-cyan-500/50 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/50'}`}>
                                    <div className="text-xs font-medium text-slate-300">Free</div>
                                    <div className="text-xs text-slate-500 mt-1">10 + 3 lab {t('banners.limitExhausted.perDay')}</div>
                                </div>
                                <div className={`p-2 rounded border ${tier === 'pro' ? 'border-blue-500/50 bg-blue-500/10' : 'border-slate-700 bg-slate-800/50'}`}>
                                    <div className="text-xs font-medium text-blue-400">Pro</div>
                                    <div className="text-xs text-slate-500 mt-1">50 + 20 lab {t('banners.limitExhausted.perDay')}</div>
                                </div>
                                <div className={`p-2 rounded border ${tier === 'business' ? 'border-purple-500/50 bg-purple-500/10' : 'border-slate-700 bg-slate-800/50'}`}>
                                    <div className="text-xs font-medium text-purple-400">Business</div>
                                    <div className="text-xs text-slate-500 mt-1">{t('banners.limitExhausted.unlimited')}</div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <span>{t('banners.limitExhausted.cancelAnytime')}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LimitExhaustedBanner;
